import {
  BOARD_GROUPINGS,
  BOARD_SORTS,
  DEFAULT_GROUPING,
  DEFAULT_SORT,
  type BoardFilters,
  type BoardGrouping,
  type BoardSort,
} from './board-utils';

/**
 * Filters travel in the query string so a board view can be pasted to a
 * colleague: `?q=moon&from=7&to=90&setters=12,34&grades=6a,6b&colors=Yellow&group=grade`.
 *
 * The age range is only written when it is narrower than the data's own bounds,
 * so an untouched board keeps a clean URL, and reading back falls to those
 * bounds whenever a param is missing or malformed.
 */

const NAME = 'q';
const AGE_FROM = 'from';
const AGE_TO = 'to';
const SETTERS = 'setters';
const GRADES = 'grades';
const COLORS = 'colors';
const GROUPING = 'group';
const SORT = 'sort';

const FILTER_PARAMS = [NAME, AGE_FROM, AGE_TO, SETTERS, GRADES, COLORS];

function readList(params: URLSearchParams, key: string): string[] {
  const raw = params.get(key);
  if (!raw) return [];

  return raw
    .split(',')
    .map((value) => value.trim())
    .filter((value) => value.length > 0);
}

function readNumber(params: URLSearchParams, key: string, fallback: number): number {
  const raw = params.get(key)?.trim();
  // `Number('')` is 0, not NaN, so a bare `?to=` would silently read as day zero
  // and hide the whole board.
  if (!raw) return fallback;

  const value = Number(raw);
  return Number.isFinite(value) ? value : fallback;
}

/** Filters described by the URL, falling back to `bounds` for the age range. */
export function filtersFromParams(
  params: URLSearchParams,
  bounds: [number, number]
): BoardFilters {
  const from = readNumber(params, AGE_FROM, bounds[0]);
  const to = readNumber(params, AGE_TO, bounds[1]);

  return {
    name: params.get(NAME)?.trim() ?? '',
    // A range the wrong way round (or outside the data) would filter everything
    // away; clamp it back into the board's own bounds instead.
    ageRange: [
      Math.min(Math.max(from, bounds[0]), bounds[1]),
      Math.max(Math.min(to, bounds[1]), bounds[0]),
    ],
    setterIds: readList(params, SETTERS),
    grades: readList(params, GRADES),
    holdColors: readList(params, COLORS),
  };
}

/**
 * Writes the filters onto a copy of `params`, dropping the ones left at their
 * default so the URL only ever carries what was actually chosen. Other query
 * params on the page are preserved.
 */
export function filtersToParams(
  filters: BoardFilters,
  bounds: [number, number],
  params: URLSearchParams
): URLSearchParams {
  const next = new URLSearchParams(params);
  FILTER_PARAMS.forEach((key) => next.delete(key));

  const name = filters.name.trim();
  if (name.length > 0) next.set(NAME, name);
  if (filters.ageRange[0] > bounds[0]) next.set(AGE_FROM, String(filters.ageRange[0]));
  if (filters.ageRange[1] < bounds[1]) next.set(AGE_TO, String(filters.ageRange[1]));
  if (filters.setterIds.length > 0) next.set(SETTERS, filters.setterIds.join(','));
  if (filters.grades.length > 0) next.set(GRADES, filters.grades.join(','));
  if (filters.holdColors.length > 0) next.set(COLORS, filters.holdColors.join(','));

  return next;
}

/**
 * The grouping the URL asks for. Kept out of `BoardFilters` on purpose: it does
 * not hide any climb, so resetting the filters must not put the columns back to
 * zones under the reader.
 */
export function groupingFromParams(params: URLSearchParams): BoardGrouping {
  const raw = params.get(GROUPING)?.trim() as BoardGrouping | undefined;

  return raw && BOARD_GROUPINGS.includes(raw) ? raw : DEFAULT_GROUPING;
}

/** Writes the grouping onto a copy of `params`, leaving the default unwritten. */
export function groupingToParams(
  grouping: BoardGrouping,
  params: URLSearchParams
): URLSearchParams {
  const next = new URLSearchParams(params);

  if (grouping === DEFAULT_GROUPING) next.delete(GROUPING);
  else next.set(GROUPING, grouping);

  return next;
}

/**
 * The order the URL asks for. Out of `BoardFilters` for the same reason as the
 * grouping: it hides nothing, so a reset must not reshuffle the columns.
 */
export function sortFromParams(params: URLSearchParams): BoardSort {
  const raw = params.get(SORT)?.trim() as BoardSort | undefined;

  return raw && BOARD_SORTS.includes(raw) ? raw : DEFAULT_SORT;
}

/** Writes the sort onto a copy of `params`, leaving the default unwritten. */
export function sortToParams(sort: BoardSort, params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);

  if (sort === DEFAULT_SORT) next.delete(SORT);
  else next.set(SORT, sort);

  return next;
}
